import { useEffect, useRef } from 'react';

import { Icon, Spinner } from '@/components/ui';
import { conversationSubject, isSideThread } from '@/features/messages/subject';
import { cn } from '@/utils/cn';
import { formatFileSize, relativeTime } from '@/utils/format';

/**
 * One conversation, oldest at the top.
 *
 * Which way a bubble points comes from `sender_side` — the fact the backend
 * stores on every message — rather than from comparing ids in the browser. A
 * recruiter who has since left the company still has their messages rendered
 * correctly, because the side was recorded when they sent them.
 *
 * `conversation` is optional; when given, a question asked before applying is
 * labelled at the top of the thread.
 */
export function Thread({ messages, isTyping, onDownload, downloadingId, conversation }) {
  const endRef = useRef(null);

  // Follow the conversation down as it grows, the way every chat does. Only
  // the box the thread sits in moves (both callers wrap it in one):
  // `scrollIntoView` scrolled the page too, so an application page opened
  // with its thread halfway down.
  useEffect(() => {
    const box = endRef.current?.parentElement?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages.length, isTyping]);

  return (
    <div className="flex flex-col gap-4">
      {/* A question about a listing says so at the top, so neither side
          mistakes it for the application's own thread. */}
      {isSideThread(conversation) && (
        <p className="bg-brand-50 text-brand-700 ring-brand-100 flex items-center gap-2 self-center rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset">
          <Icon name="chat" size={14} className="shrink-0" />
          {conversationSubject(conversation)}
        </p>
      )}
      {messages.map((message) => {
        const mine = message.is_mine;
        return (
          <article
            key={message.id}
            className={cn(
              'flex max-w-[85%] flex-col sm:max-w-[75%]',
              mine ? 'self-end items-end' : 'self-start items-start',
            )}
          >
            <header className="mb-1 flex items-baseline gap-2 px-1">
              <span className="text-ink text-xs font-semibold">{message.sender_name}</span>
              <span className="text-ink-muted text-[11px]">{relativeTime(message.created_at)}</span>
            </header>

            <div
              className={cn(
                'rounded-2xl px-4 py-2.5 shadow-xs',
                // The corner nearest the sender is squared off: the "tail"
                // that says which side a bubble came from.
                mine
                  ? 'bg-brand-600 rounded-br-md text-white'
                  : 'bg-surface border-line text-ink rounded-bl-md border',
              )}
            >
              {message.body && (
                <p className="text-sm leading-6 whitespace-pre-wrap">{message.body}</p>
              )}

              {message.attachments.map((attachment) => (
                <button
                  key={attachment.id}
                  type="button"
                  onClick={() => onDownload(attachment)}
                  disabled={downloadingId === attachment.id}
                  aria-label={`Download ${attachment.file_name}`}
                  className={cn(
                    'mt-2 flex w-full min-w-56 items-center gap-3 rounded-xl px-3 py-2 text-left transition',
                    mine
                      ? 'bg-white text-ink hover:bg-white/90'
                      : 'bg-surface-muted hover:bg-brand-50',
                  )}
                >
                  <span className="bg-brand-50 text-brand-600 grid size-9 shrink-0 place-items-center rounded-lg">
                    <Icon name="document" size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-ink block truncate text-sm font-medium">
                      {attachment.file_name}
                    </span>
                    <span className="text-ink-muted block text-xs">
                      {formatFileSize(attachment.size_bytes)}
                    </span>
                  </span>
                  {downloadingId === attachment.id ? (
                    <Spinner className="size-4" />
                  ) : (
                    <Icon name="download" size={17} className="text-ink-muted" />
                  )}
                </button>
              ))}
            </div>
          </article>
        );
      })}

      {isTyping && (
        <div
          className="bg-surface border-line flex items-center gap-1 self-start rounded-2xl rounded-bl-md border px-4 py-3"
          aria-live="polite"
          aria-label="Typing"
        >
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              className="bg-ink-muted/60 size-1.5 animate-bounce rounded-full"
              style={{ animationDelay: `${delay}ms` }}
            />
          ))}
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
